import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Typography,
  Grid,
  Drawer,
  useMediaQuery,
  IconButton,
  Button,
} from "@mui/material";
import { Menu, WarningAmber, Close } from "@mui/icons-material";
import CategoryBrandMenu from "./CategoryBrandMenu";
import ProductCard from "./ProductCard";

export default function CategoriesSelect({ category, section, setSelectedCategory }) {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [brands, setBrands] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState([]);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showAlert, setShowAlert] = useState(true);
  const isMobile = useMediaQuery("(max-width:600px)");
  const categoryTitleRef = useRef(null);

  useEffect(() => {
    fetchProducts();
  }, [category, section]);

  useEffect(() => {
    if (!selectedBrand.length) setFilteredProducts(products);
    else setFilteredProducts(products.filter((p) => selectedBrand.includes(p.brand)));
  }, [selectedBrand, products]);

  useEffect(() => {
    if (!categoryTitleRef.current) return;
    const yOffset = -120;
    const y =
      categoryTitleRef.current.getBoundingClientRect().top +
      window.pageYOffset +
      yOffset;
    window.scrollTo({ top: y, behavior: "smooth" });
  }, [category, section]);

  const fetchProducts = async () => {
    if (!section || !category) {
      setProducts([]);
      setFilteredProducts([]);
      return;
    }
    try {
      const r = await fetch("/productos/ver-productos/");
      if (!r.ok) throw new Error();
      const data = await r.json();
      const prods = data.products
        .filter((p) => p.section === section.name && p.category === category.name)
        .map((p) => ({
          ...p,
          variants: p.variants || [],
          tipo_promocion: p.tipo_promocion || null,
          valor_promocion: p.valor_promocion || null,
          precio_descuento: p.precio_descuento || null,
        }));
      setProducts(prods);
      setFilteredProducts(prods);
      const uniqueBrands = [...new Set(prods.map((p) => p.brand))].map((b) => ({ name: b }));
      setBrands(uniqueBrands);
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <Box
      sx={{
        p: { xs: 2, sm: 3 },
        bgcolor: "#fff",
        borderRadius: 2,
        boxShadow: 2,
        maxWidth: "100%",
        mx: "auto",
      }}
    >
      <Box
        sx={{
          width: "100%",
          borderRadius: 2,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: { xs: 120, sm: 200 },
          mb: 2,
          overflow: "hidden",
        }}
      >
        {section?.image && (
          <Box
            component="img"
            src={section.image}
            alt={section.name}
            sx={{
              width: { xs: "70%", sm: "auto" },
              height: "100%",
              maxHeight: { xs: 200, sm: 300 },
              objectFit: "contain",
              margin: "auto",
              display: "block",
            }}
          />
        )}
      </Box>

      <Typography
        ref={categoryTitleRef}
        align="center"
        fontWeight="bold"
        sx={{
          fontSize: { xs: "0.9rem", sm: "1.5rem", md: "2rem" },
          backgroundColor: "black",
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
          mt: 1,
          mb: 1,
        }}
      >
        {category?.name || "Categoría no encontrada"}
      </Typography>

      <Typography
        align="center"
        sx={{
          fontSize: { xs: "0.8rem", sm: "1.1rem" },
          fontWeight: "bold",
          color: "#777",
          mb: 2,
        }}
      >
        {section?.name || "Sección no encontrada"}
      </Typography>

      {isMobile && (
        <Box sx={{ display: "flex", justifyContent: "flex-start", mb: 2 }}>
          <Button
            variant="outlined"
            startIcon={<Menu />}
            onClick={() => setMobileMenuOpen(true)}
            sx={{ textTransform: "none", fontWeight: "bold" }}
          >
            Filtros
          </Button>
        </Box>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12} md={2.5} sx={{ display: { xs: "none", md: "block" } }}>
          <CategoryBrandMenu
            section={section}
            products={products}
            selectedCategory={category}
            selectedBrand={selectedBrand}
            setSelectedCategory={setSelectedCategory}
            setSelectedBrand={setSelectedBrand}
          />
        </Grid>

        <Grid item xs={12} md={9.5}>
          {showAlert && (
            <Box
              sx={{
                p: 2,
                mb: 2,
                border: "1px solid #ffb74d",
                backgroundColor: "#fff3e0",
                borderRadius: 2,
                display: "flex",
                alignItems: "flex-start",
                position: "relative",
              }}
            >
              <WarningAmber sx={{ color: "#ff9800", mr: 1, mt: "3px" }} />
              <Box>
                <Typography variant="body2" sx={{ fontWeight: "bold", color: "#333" }}>
                  Los precios que se muestran en el buscador no incluyen costos adicionales.
                </Typography>
                <Typography variant="body2" sx={{ color: "#333" }}>
                  Haz clic en el producto para ver el precio final con envío e impuestos incluidos.
                </Typography>
              </Box>
              <IconButton
                sx={{ position: "absolute", right: 8, top: 8 }}
                onClick={() => setShowAlert(false)}
              >
                <Close />
              </IconButton>
            </Box>
          )}

          {filteredProducts.length ? (
            <Grid container spacing={2}>
              {filteredProducts.map((p) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={p.id}>
                  <ProductCard product={p} />
                </Grid>
              ))}
            </Grid>
          ) : (
            <Typography align="center" sx={{ width: "100%", mt: 2, color: "#777" }}>
              No hay productos disponibles en esta categoría.
            </Typography>
          )}
        </Grid>
      </Grid>

      <Drawer
        anchor="bottom"
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        PaperProps={{ sx: { borderRadius: "10px 10px 0 0" } }}
      >
        <CategoryBrandMenu
          section={section}
          products={products}
          selectedCategory={category}
          selectedBrand={selectedBrand}
          setSelectedCategory={setSelectedCategory}
          setSelectedBrand={setSelectedBrand}
        />
      </Drawer>
    </Box>
  );
}
